import { Card, Paper, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import classnames from "classnames";
import PropTypes from "prop-types";
import Labeled from "ra-ui-materialui/lib/input/Labeled";
import CardContentInner from "ra-ui-materialui/lib/layout/CardContentInner";
import React, { Children, isValidElement } from "react";

export const FlexBreak = (props) => {
	return <div style={{ flexBasis: "100%", height: 0 }}></div>;
};


const useFlexBlockStyles = makeStyles((theme) => ({
  flexBlock: {
    width: "45%",
    display: "flex",
    flexDirection: "column",
    minHeight: "200px",
    padding: "16px",
    borderRadius: "8px",
    marginBottom: "16px",

    [theme.breakpoints.down("md")]: {
      width: "95%",
      marginBottom: "8px",
    },
    [theme.breakpoints.down("sm")]: {
      width: "90%",
      marginBottom: "8px",
    },
  },

  blockLeft: (props) => ({
    backgroundColor: props.bgColor ? props.bgColor : "#f5f5f5",
    border: props.borderColor ? `1px solid ${props.borderColor}` : "1px solid #ddd",
  }),

  blockRight: (props) => ({
    backgroundColor: props.bgColor ? props.bgColor : "#ffebee",
    border: props.borderColor ? `1px solid ${props.borderColor}` : "1px solid #ffcdd2",
  }),

  blockTitle: {
    fontSize: "14px",
    fontWeight: 600,
    marginBottom: "12px",
    color: "#333",
  },

  blockContent: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    flex: 1,
  },

  blockRow: (props) => ({
    display: "flex",
    flexWrap: "wrap",
    columnGap: "1em",
    rowGap: "8px",
    alignItems: "flex-start",
    justifyContent: props.justify ?? "space-between",

    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
      gap: "8px",
    },
  }),
}));




// FlexBlockRow component for organizing fields in rows within blocks
export const FlexBlockRow = ({
	basePath,
	record,
	resource,
	version,
	flexLayoutClasses,
	justify = "space-between", 
	...props
}) => {
	const classes = useFlexBlockStyles({ justify: justify});
	return (
		<div className={classes.blockRow} {...props}>
			{React.Children.map(props.children, (field) =>
				cloneField(field, flexLayoutClasses, {
					basePath,
					record,
					resource,
					version,
				})
			)}
		</div>
	);
};

export const FlexBlock = ({
	basePath,
	record,
	resource,
	version,
	flexLayoutClasses,
	title,
	variant = "left", // "left" or "right"
	bgColor = null,
	borderColor = null,
	...props
}) => {
	const classes = useFlexBlockStyles({ bgColor : bgColor, borderColor: borderColor });
	const blockClasses = classnames(
		classes.flexBlock,
		variant === "left" ? classes.blockLeft : classes.blockRight
	);

	return (
		<div className={blockClasses}>
			{title && <Typography variant="h6" className={classes.blockTitle}>{title}</Typography>}
			<div className={classes.blockContent}>
				{React.Children.map(props.children, (child) => {
					if (child.type === FlexBlockRow) {
						return React.cloneElement(child, {
							...child.props,
							flexLayoutClasses,
							basePath,
							record,
							resource,
							version,
						});
					}
					
					// If it's a regular field, wrap it in a row
					return (
						<div className={classes.blockRow}>
							{cloneField(child, flexLayoutClasses, {
								basePath,
								record,
								resource,
								version,
							})}
						</div>
					);
				})}
			</div>
		</div>
	);
};

// Container for side-by-side blocks
const useFlexBlockContainerStyles = makeStyles((theme) => ({
	container: {
		display: "flex",
		gap: "16px",
		width: "100%",
		alignItems: "flex-start", // Align to top
		
		[theme.breakpoints.down("md")]: {
			flexDirection: "column",
			gap: "8px",
		},
	},
}));

export const FlexBlockContainer = ({ children, ...props }) => {
	const classes = useFlexBlockContainerStyles();
	
	return (
		<div className={classes.container}>
			{React.Children.map(children, (child) =>
				React.cloneElement(child, props)
			)}
		</div>
	);
};

const useFlexRowStyles = (flexBasisValue, wrapText) =>
	makeStyles((theme) => ({
		flexRow: {
			display: "flex",
			flexBasis: flexBasisValue,
			columnGap: "2em",
			rowGap: "1em",
			flexWrap: "wrap",

			[theme.breakpoints.down("sm")]: {
				flexBasis: "100%",
				flexWrap: "wrap",
			},
		},
	}));

export const FlexRow = ({
	basePath,
	record,
	resource,
	version,
	flexLayoutClasses,
	isNot3rdPartyRole,
	wrapText = false,
	flatRow = false,
	...props
}) => {
	const classes = useFlexRowStyles(
		!wrapText ? (isNot3rdPartyRole ? "63%" : "70%") : "100%",
		wrapText
	)();

	const content = (
		<div className={classes.flexRow}>
			{React.Children.map(props.children, (field) =>
				cloneField(field, flexLayoutClasses, {
					basePath,
					record,
					resource,
					version,
				})
			)}
		</div>
	);

	return wrapText ? (
		<Paper
			elevation={1}
			style={{
				width: "100%",
				backgroundColor: "#f5f5f5",
				padding: "16px",
				marginTop: "16px",
				border: "1px solid #ddd",
				borderRadius: 8,
			}}
		>
			{content}
		</Paper>
	) : flatRow ? (
		<Paper
			elevation={1}
			style={{
				width: "100%",
				padding: "16px 0",
				boxShadow: "none"
			}}
		>
			{content}
		</Paper>
	) : (
		content
	);
};

const useFlexLayoutStyles = makeStyles(
	(theme) => ({
		wrapper: {
			// display: "flex",
			flexWrap: "wrap",
			columnGap: "2em",
			width: "100%",
			paddingLeft: "0 !important",
			paddingRight: "0 !important",
			paddingBottom: "0 !important",
		},
		break: {
			flexBasis: "100% !important",
			height: 0,
		},
		base: {
			flexBasis: "90%",
		},
		item: {
			maxWidth: "100%",
			minWidth: "100%",

			[theme.breakpoints.up("sm")]: {
				maxWidth: "15%",
				minWidth: "15%",
			},
		},
		flex: {
			flex: 1,
			maxWidth: "100%",
			minWidth: "100%",

			[theme.breakpoints.up("sm")]: {
				maxWidth: "auto",
				minWidth: "auto",
			},
		},
		warning: {
			backgroundColor: "rgba(255,0,0,0.1)",
			boxSizing: "border-box",
			paddingLeft: 8,
		},
	}),
	{ name: "FlexLayout" }
);

const sanitizeRestProps = ({
	children,
	className,
	record,
	resource,
	basePath,
	version,
	initialValues,
	translate,
	...rest
}) => rest;

const cloneField = (field, classes, { basePath, record, resource, version }) => {
	if (!field) return null;

	const divClasses = classnames(
		`ra-field ra-field-${field.props.source}`,
		field.props.className,
		field.props.flex ? classes.flex : classes.item,
		field.props.styleWarning ? classes.warning : undefined
	);

	return (
		<div key={field.props.source} className={divClasses}>
			{field.props.addLabel ? (
				<Labeled
					record={record}
					resource={resource}
					basePath={basePath}
					label={field.props.label}
					source={field.props.source}
					disabled={false}
					fullWidth={field.props.fullWidth}
				>
					{field}
				</Labeled>
			) : typeof field.type === "string" ? (
				field
			) : (
				React.cloneElement(field, { record, resource, basePath })
			)}
		</div>
	);
};

export const FlexLayout = ({
	basePath,
	className,
	children,
	record,
	resource,
	version,
	...rest
}) => {
	const classes = useFlexLayoutStyles();
	return (
		<CardContentInner
			className={classnames(className, classes.wrapper)}
			key={version}
			{...sanitizeRestProps(rest)}
		>
			{Children.map(children, (field) => {
				if (!field) {
					return null;
				}

				if (field.type === FlexBreak) {
					return <div>{field}</div>;
				}

				if (field.type === FlexRow) {
					return React.cloneElement(field, {
						...field.props,
						flexLayoutClasses: classes,
						basePath,
						record,
						resource,
						version,
						...rest,
					});
				}

				if (field.type === FlexBlock) {
					return React.cloneElement(field, {
						...field.props,
						flexLayoutClasses: classes,
						basePath,
						record,
						resource,
						version,
						...rest,
					});
				}

				if (field.type === FlexBlockContainer) {
					return React.cloneElement(field, {
						...field.props,
						flexLayoutClasses: classes,
						basePath,
						record,
						resource,
						version,
						...rest,
					});
				}

				if (!isValidElement(field)) {
					return null;
				}

				return cloneField(field, classes, { basePath, record, resource, version });
			})}
		</CardContentInner>
	);
};

FlexLayout.propTypes = {
	basePath: PropTypes.string,
	className: PropTypes.string,
	children: PropTypes.node,
	resource: PropTypes.string,
	version: PropTypes.number,
};